/**
 * Shell-level UI primitives.
 *
 * The join/lobby flow reaches for the same `Screen` and `Button` as the game
 * screens — one status row, one button ladder, one set of metrics — so those two
 * are re-exported from `components/ui` rather than reimplemented here. What
 * stays local is the smaller furniture that only the pre-game flow uses.
 */
import type { ReactNode } from 'react';

import { Eyebrow } from '../components/ui.js';

export { Button, Screen, CountdownStrip, Eyebrow } from '../components/ui.js';
export type { ButtonProps, ScreenProps } from '../components/ui.js';

/* -------------------------------------------------------------------------- */
/* Layout                                                                      */
/* -------------------------------------------------------------------------- */

export function ScreenTitle({ children, kicker }: { children: ReactNode; kicker?: ReactNode }) {
  return (
    <header className="flex flex-col gap-1.5">
      {kicker ? (
        <Eyebrow>{kicker}</Eyebrow>
      ) : null}
      <h1 className="text-[1.75rem] leading-[1.15] font-semibold tracking-tight text-ink">
        {children}
      </h1>
    </header>
  );
}

export function Body({ children }: { children: ReactNode }) {
  return <p className="text-[0.8125rem] leading-relaxed text-ink-soft">{children}</p>;
}

/** Pushes the following block to the bottom of the phone column. */
export function Spacer() {
  return <div className="flex-1" />;
}

export function Card({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`rounded-card border border-line bg-cream p-4 ${className}`}>{children}</div>
  );
}

/* -------------------------------------------------------------------------- */
/* Feedback                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Failure stays quiet and local (DESIGN.md §4): a line of colour beside the
 * thing that failed, never a modal and never a full screen.
 */
export function Notice({
  tone = 'neutral',
  children,
}: {
  tone?: 'neutral' | 'warn' | 'good' | 'wait';
  children: ReactNode;
}) {
  const tones = {
    neutral: 'border-line bg-cream text-ink-soft',
    warn: 'border-brick/40 bg-cream text-brick',
    good: 'border-line bg-cream text-green',
    wait: 'border-amber/40 bg-cream text-amber',
  } as const;
  return (
    <p className={`rounded-card border px-4 py-3 text-[0.8125rem] ${tones[tone]}`} role="status">
      {children}
    </p>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex flex-col items-center gap-3 py-10" role="status" aria-live="polite">
      <span className="h-8 w-8 animate-spin rounded-full border-2 border-line border-t-green" />
      <span className="text-[0.8125rem] text-ink-soft">{label}</span>
    </div>
  );
}
